import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { HomeworkIdeaCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const ResourcesAndAssessmentStep: React.FC<
  StepComponentProps<HomeworkIdeaCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomResource, setShowCustomResource] = useState(false);
  const [showCustomFocus, setShowCustomFocus] = useState(false);

  const resourceOptions = [
    {
      id: "Digital Resources",
      emoji: "💻",
      label: t("homework.resource.digital", "Digital Resources"),
      desc: t(
        "homework.resource.digitalDesc",
        "Online content, apps, and digital tools",
      ),
    },
    {
      id: "Printed Materials",
      emoji: "📄",
      label: t("homework.resource.printed", "Printed Materials"),
      desc: t(
        "homework.resource.printedDesc",
        "Books, worksheets, and physical documents",
      ),
    },
    {
      id: "Video Content",
      emoji: "🎥",
      label: t("homework.resource.video", "Video Content"),
      desc: t(
        "homework.resource.videoDesc",
        "Educational videos and multimedia",
      ),
    },
  ];

  const focusAreaOptions = [
    {
      id: "Content Quality",
      emoji: "📚",
      label: t("homework.focus.content", "Content Quality"),
      desc: t(
        "homework.focus.contentDesc",
        "Accuracy and depth of information",
      ),
    },
    {
      id: "Creativity",
      emoji: "🎨",
      label: t("homework.focus.creativity", "Creativity"),
      desc: t(
        "homework.focus.creativityDesc",
        "Original thinking and innovation",
      ),
    },
    {
      id: "Accuracy",
      emoji: "🎯",
      label: t("homework.focus.accuracy", "Accuracy"),
      desc: t("homework.focus.accuracyDesc", "Correctness and precision"),
    },
    {
      id: "Effort",
      emoji: "💪",
      label: t("homework.focus.effort", "Effort"),
      desc: t("homework.focus.effortDesc", "Time and energy invested"),
    },
    {
      id: "Presentation",
      emoji: "🎤",
      label: t("homework.focus.presentation", "Presentation"),
      desc: t(
        "homework.focus.presentationDesc",
        "Organization and communication",
      ),
    },
  ];

  const handleResourceToggle = (resourceId: string) => {
    const currentResources = data.resourcePreferences || [];
    const isSelected = currentResources.includes(resourceId);

    if (isSelected) {
      // Remove from selection
      const newResources = currentResources.filter(
        (id: string) => id !== resourceId,
      );
      onDataChange({ resourcePreferences: newResources });
    } else {
      // Add to selection
      const newResources = [...currentResources, resourceId];
      onDataChange({ resourcePreferences: newResources });
    }
  };

  const handleResourceOthersToggle = () => {
    const currentResources = data.resourcePreferences || [];
    const hasCustomResource = currentResources.some(
      (resource: string) =>
        !resourceOptions.some((option) => option.id === resource),
    );

    if (hasCustomResource || showCustomResource) {
      // Remove custom resource
      const newResources = currentResources.filter((resource: string) =>
        resourceOptions.some((option) => option.id === resource),
      );
      onDataChange({ resourcePreferences: newResources });
      setShowCustomResource(false);
    } else {
      // Show custom resource input
      setShowCustomResource(true);
    }
  };

  const handleCustomResourceChange = (value: string) => {
    const currentResources = data.resourcePreferences || [];
    // Remove any existing custom resources first
    const filteredResources = currentResources.filter((resource: string) =>
      resourceOptions.some((option) => option.id === resource),
    );

    if (value.trim()) {
      onDataChange({
        resourcePreferences: [...filteredResources, value.trim()],
      });
    } else {
      onDataChange({ resourcePreferences: filteredResources });
    }
  };

  const handleFocusAreaToggle = (focusId: string) => {
    const currentEvaluationGuidelines = data.evaluationGuidelines || {};
    const currentFocusAreas = currentEvaluationGuidelines.focusAreas || [];
    const isSelected = currentFocusAreas.includes(focusId);

    if (isSelected) {
      // Remove from selection
      const newFocusAreas = currentFocusAreas.filter(
        (id: string) => id !== focusId,
      );
      onDataChange({
        evaluationGuidelines: {
          ...currentEvaluationGuidelines,
          focusAreas: newFocusAreas,
        },
      });
    } else {
      // Add to selection
      const newFocusAreas = [...currentFocusAreas, focusId];
      onDataChange({
        evaluationGuidelines: {
          ...currentEvaluationGuidelines,
          focusAreas: newFocusAreas,
        },
      });
    }
  };

  const handleFocusOthersToggle = () => {
    const currentEvaluationGuidelines = data.evaluationGuidelines || {};
    const currentFocusAreas = currentEvaluationGuidelines.focusAreas || [];
    const hasCustomFocus = currentFocusAreas.some(
      (focus: string) =>
        !focusAreaOptions.some((option) => option.id === focus),
    );

    if (hasCustomFocus || showCustomFocus) {
      // Remove custom focus
      const newFocusAreas = currentFocusAreas.filter((focus: string) =>
        focusAreaOptions.some((option) => option.id === focus),
      );
      onDataChange({
        evaluationGuidelines: {
          ...currentEvaluationGuidelines,
          focusAreas: newFocusAreas,
        },
      });
      setShowCustomFocus(false);
    } else {
      // Show custom focus input
      setShowCustomFocus(true);
    }
  };

  const handleCustomFocusChange = (value: string) => {
    const currentEvaluationGuidelines = data.evaluationGuidelines || {};
    const currentFocusAreas = currentEvaluationGuidelines.focusAreas || [];
    // Remove any existing custom focus areas first
    const filteredFocusAreas = currentFocusAreas.filter((focus: string) =>
      focusAreaOptions.some((option) => option.id === focus),
    );

    if (value.trim()) {
      onDataChange({
        evaluationGuidelines: {
          ...currentEvaluationGuidelines,
          focusAreas: [...filteredFocusAreas, value.trim()],
        },
      });
    } else {
      onDataChange({
        evaluationGuidelines: {
          ...currentEvaluationGuidelines,
          focusAreas: filteredFocusAreas,
        },
      });
    }
  };

  const handleTeacherGuideToggle = () => {
    const currentEvaluationGuidelines = data.evaluationGuidelines || {};
    onDataChange({
      evaluationGuidelines: {
        ...currentEvaluationGuidelines,
        includeTeacherGuidelines:
          !currentEvaluationGuidelines.includeTeacherGuidelines,
      },
    });
  };

  const handleStudentAssessmentToggle = () => {
    const currentEvaluationGuidelines = data.evaluationGuidelines || {};
    onDataChange({
      evaluationGuidelines: {
        ...currentEvaluationGuidelines,
        includeStudentCriteria:
          !currentEvaluationGuidelines.includeStudentCriteria,
      },
    });
  };

  const isResourceSelected = (resourceId: string) => {
    return (data.resourcePreferences || []).includes(resourceId);
  };

  const isFocusAreaSelected = (focusId: string) => {
    return (data.evaluationGuidelines?.focusAreas || []).includes(focusId);
  };

  const isResourceOthersSelected = () => {
    const currentResources = data.resourcePreferences || [];
    return (
      showCustomResource ||
      currentResources.some(
        (resource: string) =>
          !resourceOptions.some((option) => option.id === resource),
      )
    );
  };

  const isFocusOthersSelected = () => {
    const currentFocusAreas = data.evaluationGuidelines?.focusAreas || [];
    return (
      showCustomFocus ||
      currentFocusAreas.some(
        (focus: string) =>
          !focusAreaOptions.some((option) => option.id === focus),
      )
    );
  };

  const getCustomResourceValue = () => {
    const currentResources = data.resourcePreferences || [];
    const customResource = currentResources.find(
      (resource: string) =>
        !resourceOptions.some((option) => option.id === resource),
    );
    return customResource || "";
  };

  const getCustomFocusValue = () => {
    const currentFocusAreas = data.evaluationGuidelines?.focusAreas || [];
    const customFocus = currentFocusAreas.find(
      (focus: string) =>
        !focusAreaOptions.some((option) => option.id === focus),
    );
    return customFocus || "";
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-xl font-semibold text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
        >
          {t("homework.steps.resources.title", "Resources & Assessment")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t(
            "homework.steps.resources.description",
            "Configure resources and assessment criteria",
          )}
        </p>
      </div>

      {/* Resource Choices */}
      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("homework.form.resourceChoices", "Resource Preferences")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t(
            "homework.form.resourceChoicesDesc",
            "Select preferred resource types for this homework (multiple selection allowed)",
          )}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {resourceOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isResourceSelected(option.id)
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleResourceToggle(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                    isResourceSelected(option.id)
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {isResourceSelected(option.id) && (
                    <div className="w-2.5 h-2.5 bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{option.emoji}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Others Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isResourceOthersSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleResourceOthersToggle}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                  isResourceOthersSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isResourceOthersSelected() && (
                  <div className="w-2.5 h-2.5 bg-white"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">📝</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {t("homework.resource.others", "Other")}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t(
                    "homework.resource.othersDesc",
                    "Specify other resource preferences",
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Resource Input */}
        {isResourceOthersSelected() && (
          <div className="mt-4">
            <label
              className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
            >
              {t("homework.form.customResource", "Custom Resource")}
            </label>
            <input
              type="text"
              value={getCustomResourceValue()}
              onChange={(e) => handleCustomResourceChange(e.target.value)}
              className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
              placeholder={t(
                "homework.form.customResourcePlaceholder",
                "Describe your preferred resource type...",
              )}
            />
          </div>
        )}
      </div>

      {/* Assessment Guide */}
      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("homework.form.assessmentGuide", "Assessment Guide")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t(
            "homework.form.assessmentGuideDesc",
            "Configure assessment guidelines and criteria",
          )}
        </p>

        <div className="space-y-4">
          {/* Teacher Guide Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-lg">👨‍🏫</span>
                <h4 className="font-medium text-base">
                  {t(
                    "homework.assessment.teacherGuide",
                    "Include Teacher Guide",
                  )}
                </h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t(
                  "homework.assessment.teacherGuideDesc",
                  "Provide detailed assessment criteria for teachers",
                )}
              </p>
            </div>
            <button
              onClick={handleTeacherGuideToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                data.evaluationGuidelines?.includeTeacherGuidelines
                  ? `bg-${themeColor}-500`
                  : "bg-gray-300 dark:bg-gray-600"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  data.evaluationGuidelines?.includeTeacherGuidelines
                    ? "translate-x-6"
                    : "translate-x-1"
                }`}
              />
            </button>
          </div>

          {/* Student Self-Assessment Toggle */}
          <div className="flex items-center justify-between p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-lg">🎓</span>
                <h4 className="font-medium text-base">
                  {t(
                    "homework.assessment.studentSelf",
                    "Include Student Self-Assessment",
                  )}
                </h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {t(
                  "homework.assessment.studentSelfDesc",
                  "Include criteria for student self-assessment",
                )}
              </p>
            </div>
            <button
              onClick={handleStudentAssessmentToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                data.evaluationGuidelines?.includeStudentCriteria
                  ? `bg-${themeColor}-500`
                  : "bg-gray-300 dark:bg-gray-600"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  data.evaluationGuidelines?.includeStudentCriteria
                    ? "translate-x-6"
                    : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Focus Areas */}
      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("homework.form.focusAreas", "Focus Areas")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t(
            "homework.form.focusAreasDesc",
            "Select the key areas to focus on for assessment (multiple selection allowed)",
          )}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {focusAreaOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                isFocusAreaSelected(option.id)
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleFocusAreaToggle(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                    isFocusAreaSelected(option.id)
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {isFocusAreaSelected(option.id) && (
                    <div className="w-2.5 h-2.5 bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{option.emoji}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Others Option for Focus Areas */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isFocusOthersSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleFocusOthersToggle}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                  isFocusOthersSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isFocusOthersSelected() && (
                  <div className="w-2.5 h-2.5 bg-white"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">📝</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {t("homework.focus.others", "Other")}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("homework.focus.othersDesc", "Specify other focus areas")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Focus Area Input */}
        {isFocusOthersSelected() && (
          <div className="mt-4">
            <label
              className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
            >
              {t("homework.form.customFocus", "Custom Focus Area")}
            </label>
            <input
              type="text"
              value={getCustomFocusValue()}
              onChange={(e) => handleCustomFocusChange(e.target.value)}
              className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
              placeholder={t(
                "homework.form.customFocusPlaceholder",
                "Describe your focus area...",
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};
